import { AsyncPipe, CommonModule, NgIf } from '@angular/common';
import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { CompaniesApi, SettingsApi, TenantUsersDto, UploadsApi } from '@buspro/web/apis';
import { TenantAuthService } from '@buspro/web/core';
import { PhoneFormatPipe } from '@buspro/web/shared';
import { NgxAuthService } from '@corelabs/ng-auth';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzSpinComponent } from 'ng-zorro-antd/spin';
import { Observable, firstValueFrom, lastValueFrom } from 'rxjs';
import { ChangeCompanyNameModalComponent } from '../change-company-name-modal/change-company-name-modal.component';
import { LetterheadSettingsComponent } from './letterhead-settings.component';
import { UpdateCompanyInfoModalComponent } from '../update-company-info/update-company-info.component';

@Component({
  selector: 'tenant-company-settings',
  templateUrl: './company-settings.component.html',
  styleUrls: ['./company-settings.component.css'],
  imports: [
    CommonModule,
    NgIf,
    NzSpinComponent,
    ChangeCompanyNameModalComponent,
    AsyncPipe,
    UpdateCompanyInfoModalComponent,
    PhoneFormatPipe,
    LetterheadSettingsComponent,
  ],
  providers: [NzNotificationService],
  standalone: true,
})
export class CompanySettingsComponent {
  private authService = inject(TenantAuthService);
  private uploadsApi = inject(UploadsApi);
  private notification = inject(NzNotificationService);
  private companiesApi = inject(CompaniesApi);
  private settingsApi = inject(SettingsApi);
  private ngxAuthService = inject(NgxAuthService);
  private cdr = inject(ChangeDetectorRef);

  loading = false;
  uploading = false;

  authUser$: Observable<TenantUsersDto | null> = this.ngxAuthService.state;
  showEditCompanyNameModal = false;
  showEditCompanyInfoModal = false;
  showEditCompanyAddressModal = false;

  tenantSettingsQuery = injectQuery(() => ({
    queryKey: ['tenant-settings'],
    queryFn: () => lastValueFrom(this.settingsApi.tenantSettingsControllerGetTenantSettings()),
    staleTime: 5 * 60 * 1000,
  }));

  upload(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;

    this.uploading = true;

    // Route through the generated UploadsApi so the request targets the
    // configured API host (environment.server / rootUrl) WITH the auth token,
    // exactly like every other API call. The previous raw relative
    // `this.http.post('/api/uploads/logo', …)` resolved against the page
    // origin (e.g. dev-apps.buspro.biz), which has no /api proxy → 404.
    // The generated client builds the multipart FormData itself from { file },
    // so do NOT pre-wrap it in a FormData here.
    firstValueFrom(this.uploadsApi.uploadsControllerUploadLogo({ body: { file } }))
      .then(({ url }) => this.uploadedLogoUrl(url))
      .catch((err) => this.uploadError(err));
  }

  async uploadedLogoUrl(url: string) {
    try {
      const user = await firstValueFrom(this.authUser$);
      const companyId = user?.company.id as string;
      await firstValueFrom(
        this.companiesApi.companyControllerUpdateCompanyLogo({ id: companyId, body: { logoUrl: url, companyId } }),
      );
      this.uploading = false;
      this.cdr.detectChanges();
      this.notification.success('Success', 'Company logo updated successfully');
      this.authService.fetchUser().subscribe({
        next: (updatedUser) => {
          this.ngxAuthService.setUser(updatedUser);
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Failed to update user data', error);
        },
      });
    } catch (error) {
      this.uploading = false;
      this.cdr.detectChanges();
      this.notification.error('Error', 'Failed to update company logo');
      console.error('Error updating company logo:', error);
    }
  }

  uploadError(error: unknown) {
    this.uploading = false;
    this.cdr.detectChanges();
    this.notification.error('Error', 'Failed to upload company logo');
    console.error('upload error', error);
  }
}
